/**
 * Accounting for canvas pointer input, and the reporting built on it.
 *
 * A stroke that goes missing on an iPad leaves nothing behind: no ink, no
 * exception, and no `path:created`, because Fabric's `_finalizeAndAddPath`
 * returns silently when a stroke's points collapse to an empty path. Without
 * this module the only evidence is a student saying a letter did not appear.
 *
 * Runs ALWAYS, not only when someone is debugging — a lost stroke is reported
 * at ERROR, which no configuration can suppress. Per-contact detail is emitted
 * at INFO3 and dropped by the tracing manager unless the `canvas_input_tracing`
 * gate raised this user's level. The call sites are unconditional either way;
 * the manager decides. See docs/FeatureDesigns/canvas-input-tracing.md.
 */

import { WebLogger, WebComponentCode } from '@shared-ui/logging';
import { getPalmRejectionSetting, isPencilSeen } from '../palmRejection';

export interface ContactRecord {
    /** Stable identity: contacts are replaced, not mutated, as outcomes land. */
    seq: number;
    /** 'pen' | 'touch' as PointerEvent saw it. */
    pointerType: string;
    /** 'stylus' | 'direct' as TouchEvent saw it — the two disagree on occasion,
     *  and that disagreement is itself a defect worth catching. */
    touchType: string;
    moves: number;
    samples: number;
    width: number;
    radius: number;
    isPrimary: boolean;
    /** Did this contact end up as a committed path? */
    path: boolean;
    closed: boolean;
    cancelled: boolean;
    /** Milliseconds since the previous contact of the same kind. */
    gapMs: number | null;
    /** Furthest the contact travelled from where it landed, in CSS px. */
    travel: number;
}

export interface InputSnapshot {
    penDown: number;
    penUp: number;
    penCancel: number;
    touchDown: number;
    paths: number;
    contacts: ContactRecord[];
}

const MAX_CONTACTS = 60;

/**
 * A contact has to actually move to have been a stroke.
 *
 * Fabric discards a path with no extent on purpose — browsers render a
 * zero-length path inconsistently — so a tap producing nothing is correct
 * behaviour, not a loss. Reporting those as errors buried the real signal
 * under taps on buttons and options.
 */
const STROKE_MIN_MOVES = 3;
const STROKE_MIN_TRAVEL_PX = 8;

/**
 * Only contacts that begin on a drawing surface are the canvas's business.
 *
 * The accounting listens on window so it can see contacts the canvas never
 * receives, which also means it sees every tap elsewhere on the page. Without
 * this scope a pen tap on a nav button reports as a lost stroke.
 */
function isDrawingSurface(target: EventTarget | null): boolean {
    return target instanceof HTMLCanvasElement && !!target.closest('.canvas-container');
}

const logger = new WebLogger(WebComponentCode.WEB_CANVAS, 'input');

let state: InputSnapshot = { penDown: 0, penUp: 0, penCancel: 0, touchDown: 0, paths: 0, contacts: [] };
let installed = false;
let lastPenDownAt: number | null = null;
let seqCounter = 0;
/** Where the open contact landed, for measuring how far it travelled. */
let origin: { x: number; y: number } | null = null;
/** The pointer the open contact belongs to. Without this, a second finger
 *  elsewhere on the page would have its movement attributed to the stroke. */
let activePointerId: number | null = null;
/** Stylus contacts the scope rejected — see the session summary. */
let offSurface = 0;
const subscribers = new Set<(s: InputSnapshot) => void>();

/**
 * Supplied by the live engine, so a report can say what was actually mounted
 * and configured when a stroke went missing.
 *
 * Prod showed losses whose pointer was not the browser's primary — on a build
 * that exempts pens from Fabric's primacy test. Either the exemption was not in
 * effect on that canvas or something else discards the stroke, and guessing
 * between those from here has already been wrong once.
 */
let diagnosticsProbe: (() => Record<string, unknown>) | null = null;

export function setCanvasDiagnosticsProbe(fn: (() => Record<string, unknown>) | null): void {
    diagnosticsProbe = fn;
}

function engineDiagnostics(): Record<string, unknown> {
    if (!diagnosticsProbe) return { engine: 'none' };
    try {
        return diagnosticsProbe();
    } catch {
        return { engine: 'probe-failed' };
    }
}

function armedState(): string {
    return `${isPencilSeen() ? 'seen' : 'not-seen'}/${getPalmRejectionSetting()}`;
}

function publish(): void {
    for (const cb of subscribers) {
        try { cb(state); } catch { /* a bad subscriber must not break input */ }
    }
}

function mutate(fn: (draft: InputSnapshot) => void): void {
    const next: InputSnapshot = { ...state, contacts: state.contacts.slice() };
    fn(next);
    state = next;
    publish();
}

/** The newest contact still awaiting its outcome. */
function openContact(draft: InputSnapshot): ContactRecord | undefined {
    const last = draft.contacts[draft.contacts.length - 1];
    return last && !last.closed ? last : undefined;
}

/**
 * Judge a contact once the event turn is over.
 *
 * The accounting listens on window in the capture phase, which runs BEFORE
 * Fabric's own document-level pointerup — so at close time the stroke has not
 * yet had its chance to commit and every stroke would look lost. Deferring by a
 * task lets `path:created` land first. The contact is re-read from state rather
 * than captured, because outcomes replace records instead of mutating them.
 */
function scheduleOutcome(seq: number): void {
    setTimeout(() => {
        const c = state.contacts.find(x => x.seq === seq);
        if (c) reportOutcome(c);
    }, 0);
}

function reportOutcome(c: ContactRecord): void {
    const detail = { ...c, armed: armedState(), ...engineDiagnostics() };
    if (c.path) {
        logger.info3('canvas contact drew', detail);
        return;
    }
    // A contact that produced no path. For a pen this is the bug we are hunting;
    // for a touch it is usually palm rejection doing its job, and only notable
    // when the contact carried a real stroke's worth of movement.
    const looksLikeAStroke = c.moves >= STROKE_MIN_MOVES && c.travel >= STROKE_MIN_TRAVEL_PX;
    if (!looksLikeAStroke) {
        // Never moved far enough to be a stroke — a tap, or a contact that
        // landed and lifted. Nothing was lost.
        logger.info3('canvas contact discarded', detail);
        return;
    }
    if (c.pointerType === 'pen' || c.touchType === 'stylus') {
        logger.error('canvas stroke lost', undefined, {
            ...detail,
            // Named explicitly because it is the first thing to look at: a
            // stylus arriving as a touch pointer is blocked by our own guard.
            mislabelledStylus: c.pointerType === 'touch' && c.touchType === 'stylus',
        });
    } else {
        logger.info2('canvas touch stroke blocked', detail);
    }
}

function closeContact(draft: InputSnapshot, cancelled: boolean): void {
    const open = openContact(draft);
    if (!open) return;
    const i = draft.contacts.length - 1;
    draft.contacts[i] = { ...open, closed: true, cancelled };
    scheduleOutcome(open.seq);
}

/**
 * Install the window-level accounting. Idempotent, and safe to call before any
 * canvas exists — it observes only, never suppresses, and the input guard does
 * not suppress pen events, so it cannot perturb what it measures.
 */
export function installCanvasInputTelemetry(): () => void {
    if (installed) return () => {};
    installed = true;

    const onPointerDown = (e: PointerEvent) => {
        if (e.pointerType !== 'pen' && e.pointerType !== 'touch') return;
        if (!isDrawingSurface(e.target)) {
            // A stylus contact that the scope rejected. Reported because the
            // alternative explanation for a stroke with no record — that the
            // browser never delivered it — is unfalsifiable from in here, and
            // this is the half that is ours to check. A pen rarely taps
            // anything but the canvas, so this stays quiet in normal use.
            if (e.pointerType === 'pen') {
                const t = e.target as Element | null;
                logger.info3('canvas contact off-surface', {
                    tag: t?.tagName ?? 'none',
                    cls: (t instanceof Element ? t.className : '')?.toString().slice(0, 60),
                    inContainer: t instanceof Element ? !!t.closest('.canvas-container') : false,
                    x: Math.round(e.clientX),
                    y: Math.round(e.clientY),
                });
                offSurface++;
            }
            return;
        }
        origin = { x: e.clientX, y: e.clientY };
        activePointerId = e.pointerId;
        const now = Date.now();
        const gapMs = e.pointerType === 'pen' && lastPenDownAt !== null ? now - lastPenDownAt : null;
        if (e.pointerType === 'pen') lastPenDownAt = now;
        mutate(draft => {
            // A contact still open when the next one starts never got an
            // outcome; close it so it is reported rather than lost silently.
            closeContact(draft, false);
            if (e.pointerType === 'pen') draft.penDown++; else draft.touchDown++;
            draft.contacts = [...draft.contacts, {
                seq: ++seqCounter,
                pointerType: e.pointerType,
                touchType: '?',
                moves: 0,
                samples: 0,
                width: e.width,
                radius: 0,
                isPrimary: e.isPrimary,
                path: false,
                closed: false,
                cancelled: false,
                gapMs,
                travel: 0,
            }].slice(-MAX_CONTACTS);
        });
    };

    const onPointerMove = (e: PointerEvent) => {
        if (e.pointerId !== activePointerId) return;
        mutate(draft => {
            const c = openContact(draft);
            if (!c) return;
            draft.contacts[draft.contacts.length - 1] = {
                ...c,
                moves: c.moves + 1,
                samples: c.samples + (e.getCoalescedEvents?.().length ?? 1),
                width: Math.max(c.width, e.width),
                travel: origin
                    ? Math.max(c.travel, Math.hypot(e.clientX - origin.x, e.clientY - origin.y))
                    : c.travel,
            };
        });
    };

    const onPointerUp = (e: PointerEvent) => {
        if (e.pointerId !== activePointerId) return;
        activePointerId = null;
        mutate(draft => {
            if (e.pointerType === 'pen') draft.penUp++;
            closeContact(draft, false);
        });
    };

    const onPointerCancel = (e: PointerEvent) => {
        if (e.pointerId !== activePointerId) return;
        activePointerId = null;
        mutate(draft => {
            if (e.pointerType === 'pen') draft.penCancel++;
            closeContact(draft, true);
        });
    };

    // WebKit dispatches pointerdown BEFORE touchstart for the same contact, so
    // the true identity of a contact lands one event after the pointer that
    // carries it. That ordering is why the guard cannot consult touchType at
    // block time, and why it is stamped on afterwards here.
    const onTouchStart = (e: TouchEvent) => {
        if (activePointerId === null) return;
        const t = e.changedTouches[0] as (Touch & { touchType?: string }) | undefined;
        if (!t) return;
        mutate(draft => {
            const c = openContact(draft);
            if (!c || c.touchType !== '?') return;
            draft.contacts[draft.contacts.length - 1] = {
                ...c,
                touchType: t.touchType ?? '?',
                radius: Math.max(t.radiusX ?? 0, t.radiusY ?? 0),
            };
        });
    };

    const onPageHide = () => emitSessionSummary();

    const opts = { capture: true, passive: true } as const;
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pointerdown', onPointerDown, opts);
    window.addEventListener('pointermove', onPointerMove, opts);
    window.addEventListener('pointerup', onPointerUp, opts);
    window.addEventListener('pointercancel', onPointerCancel, opts);
    window.addEventListener('touchstart', onTouchStart, opts);

    return () => {
        installed = false;
        emitSessionSummary();
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pointerdown', onPointerDown, opts);
        window.removeEventListener('pointermove', onPointerMove, opts);
        window.removeEventListener('pointerup', onPointerUp, opts);
        window.removeEventListener('pointercancel', onPointerCancel, opts);
        window.removeEventListener('touchstart', onTouchStart, opts);
    };
}

/** Called by the engine when a stroke commits, so contacts can be reconciled. */
export function noteCanvasPathCreated(): void {
    mutate(draft => {
        draft.paths++;
        for (let i = draft.contacts.length - 1; i >= 0; i--) {
            if (!draft.contacts[i].path) { draft.contacts[i] = { ...draft.contacts[i], path: true }; break; }
        }
    });
}

/**
 * One row per session that touched a canvas, at a level everyone sends.
 *
 * Per-contact detail is gated, so without this a session is invisible unless it
 * errored — and the shape of the problem is a RATIO ("a third of my strokes did
 * not draw") which no single-contact record can express. Emitted on pagehide
 * rather than unload because iOS Safari frequently skips unload entirely.
 */
function emitSessionSummary(): void {
    if (state.penDown === 0 && state.touchDown === 0) return;
    const drew = state.contacts.filter(c => c.path).length;
    const lostPen = state.contacts.filter(c => c.pointerType === 'pen' && c.closed && !c.path).length;
    logger.info('canvas session summary', {
        penDown: state.penDown,
        penUp: state.penUp,
        penCancel: state.penCancel,
        touchDown: state.touchDown,
        paths: state.paths,
        drew,
        lostPen,
        offSurface,
        armed: armedState(),
        ...engineDiagnostics(),
    });
}

export function subscribeCanvasInput(cb: (s: InputSnapshot) => void): () => void {
    subscribers.add(cb);
    cb(state);
    return () => { subscribers.delete(cb); };
}

export function resetCanvasInputStats(): void {
    lastPenDownAt = null;
    offSurface = 0;
    state = { penDown: 0, penUp: 0, penCancel: 0, touchDown: 0, paths: 0, contacts: [] };
    publish();
}
